import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAppointmentById_apiCalls } from "../../../api/api_calls/appointment_apiCalls";
import {
  formatDateReadable,
  formatTime,
} from "../../../helpers/dateTimeHelpers";
import { Button } from "../../../components/ui/button";
import { useToast } from "../../../hooks/use-toast";
import TwillioVideoComponent from "../../sharedscreens/twilio-call-panel/TwillioVideoComponent";

const PatientVideoCallPanel = () => {
  const [appointmentData, setAppointmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const { toast } = useToast();
  const appointmentId =
    location.state?.appointmentId || location.state?.appointment?.id;

  // const user = useSelector((state) => state.userReducer.user); // Not currently used

  // Fetch appointment data
  const fetchAppointmentData = useCallback(async () => {
    if (!appointmentId) {
      const errorMsg = "No appointment ID provided";
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await getAppointmentById_apiCalls({ id: appointmentId });
      if (response?.data) {
        setAppointmentData(response.data);
        setError(null);
      } else {
        throw new Error("No appointment data received");
      }
    } catch (error) {
      console.error("Failed to fetch appointment details:", error);
      const errorMsg =
        error.message ||
        "Failed to fetch appointment details. Please try again.";
      setError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [appointmentId, toast]);

  // Fetch appointment data on component mount
  useEffect(() => {
    fetchAppointmentData();
  }, [fetchAppointmentData]);

  // Helper function to format appointment date and time using existing helpers
  const formatAppointmentDateTime = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return { date: "N/A", time: "N/A" };

    const startDate = new Date(startDateTime);
    const endDate = new Date(endDateTime);

    // Use existing helper functions
    const formattedDate = formatDateReadable(startDateTime);
    const startTime = formatTime(startDateTime);
    const endTime = formatTime(endDateTime);
    const duration = Math.round((endDate - startDate) / (1000 * 60)); // duration in minutes

    return {
      date: formattedDate,
      time: `${startTime} – ${endTime} (${duration} minutes)`,
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-900">
        <div className="text-white text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <p>Loading appointment details...</p>
        </div>
      </div>
    );
  }

  // Error state - no appointment data
  if (!appointmentData && !loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-900">
        <div className="text-white text-center max-w-md">
          <p className="text-red-400 mb-4">
            {error || "Failed to load appointment details"}
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={fetchAppointmentData} variant="outline">
              Retry
            </Button>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </div>
        </div>
      </div>
    );
  }

  const { date, time } = formatAppointmentDateTime(
    appointmentData.startDateTime,
    appointmentData.endDateTime,
  );

  const doctorName = `Dr. ${appointmentData.doctor?.firstName || "N/A"} ${appointmentData.doctor?.lastName || ""}`;

  return (
    <div className="flex h-screen flex-col bg-gray-900 overflow-hidden">
      <div className="bg-gray-800 p-4 text-white">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-lg font-medium">{doctorName}</div>
            <div className="flex items-center mt-1">
              <div className="w-3 h-3 rounded-sm mr-2 bg-blue-500"></div>
              <span className="text-sm">Video Call</span>
            </div>
          </div>
          <div className="text-sm text-right">
            <div>{date}</div>
            <div className="mt-1">{time}</div>
          </div>
        </div>
      </div>
      <div className="flex flex-1 p-4 min-h-0 h-full">
        <TwillioVideoComponent
          toggleFormsPanel={null} // No forms panel for patients
          addparticipant={true} // Patients can't add participants
          appointmentData={appointmentData}
        />
      </div>
    </div>
  );
};

export default PatientVideoCallPanel;
